import { TypeCabinet } from '../../../constants/enum.js';

import * as THREE from 'three';
import { KitchenCabinet } from '../cabinets/KitchenCabinet.js';
import { WardrobeCabinet } from '../cabinets/WardrobeCabinet.js';
import { ShoeCabinet } from '../cabinets/ShoeCabinet.js';
import { TiviCabinet } from '../cabinets/TiviCabinet.js';
import { DecorativeCabinet } from '../cabinets/DecorativeCabinet.js';
import { BedCabinet } from '../cabinets/BedCabinet.js';
import { VanityCabinet } from '../cabinets/VanityCabinet.js';
import { DeskCabinet } from '../cabinets/DeskCabinet.js';

export class CabinetManager {
  constructor(sceneManager) {
    this.sceneManager = sceneManager;
    this.currentCabinet = null;
    this.cabinetHistory = [];
  }

  /**
   * Bắt đầu thiết kế loại tủ cụ thể
   */
  startDesign(cabinetType) {
    // Xóa thiết kế hiện tại nếu có
    if (this.currentCabinet) {
      this.currentCabinet.clearAllModules();
    }

    // Tạo cabinet mới
    switch (cabinetType) {
      case TypeCabinet.KITCHEN:
        this.currentCabinet = new KitchenCabinet(this.sceneManager);
        break;
      case TypeCabinet.WARDROBE:
        this.currentCabinet = new WardrobeCabinet(this.sceneManager);
        break;
      case TypeCabinet.SHOE:
        this.currentCabinet = new ShoeCabinet(this.sceneManager);
        break;
      case TypeCabinet.TV:
        this.currentCabinet = new TiviCabinet(this.sceneManager);
        break;
      case TypeCabinet.DECORATIVE:
        this.currentCabinet = new DecorativeCabinet(this.sceneManager);
        break;
      case TypeCabinet.BED:
        this.currentCabinet = new BedCabinet(this.sceneManager);
        break;
      case TypeCabinet.VANITY:
        this.currentCabinet = new VanityCabinet(this.sceneManager);
        break;
      case TypeCabinet.DESK:
        this.currentCabinet = new DeskCabinet(this.sceneManager);
        break;
      default:
        throw new Error(`Unknown cabinet type: ${cabinetType}`);
    }

    return this.currentCabinet;
  }

  /**
   * Thêm module vào thiết kế hiện tại
   */
  async addModule(moduleData, moduleType, yPosition) {
    if (!this.currentCabinet) {
      throw new Error(
        'No cabinet design in progress. Call startDesign() first.'
      );
    }

    return await this.currentCabinet.addModule(
      moduleData,
      moduleType,
      yPosition
    );
  }

  /**
   * Xóa module
   */
  removeModule(module) {
    if (!this.currentCabinet) return;
    this.currentCabinet.removeModule(module);
  }

  /**
   * Hoàn thành thiết kế
   */
  finishDesign() {
    if (!this.currentCabinet) return null;

    const designData = this.exportCurrentDesign();
    this.cabinetHistory.push(designData);

    const finishedCabinet = this.currentCabinet;
    this.currentCabinet = null;

    return finishedCabinet;
  }

  /**
   * Lấy cabinet hiện tại
   */
  getCurrentCabinet() {
    return this.currentCabinet;
  }

  // =============== OPTIMIZED METHODS - DELEGATE TO BASECABINET ===============

  /**
   * Lấy tất cả modules trong cabinet hiện tại
   * @returns {Array} Danh sách tất cả modules
   */
  getAllModules() {
    if (!this.currentCabinet) {
      console.warn('No active cabinet. Use startDesign() first.');
      return [];
    }

    return this.currentCabinet.getAllModules();
  }

  /**
   * Lấy modules theo type cụ thể
   * @param {string} moduleType - Loại module (lower, upper, ceiling...)
   * @returns {Array} Danh sách modules theo type
   */
  getModulesByType(moduleType) {
    if (!this.currentCabinet) {
      console.warn('No active cabinet. Use startDesign() first.');
      return [];
    }

    return this.currentCabinet.getModulesByType(moduleType);
  }

  /**
   * OPTIMIZED: Delegate to BaseCabinet
   * Lấy thông tin chi tiết của một module theo ID
   * @param {string} moduleId - ID của module
   * @returns {Object|null} Thông tin module hoặc null nếu không tìm thấy
   */
  getModuleById(moduleId) {
    if (!this.currentCabinet) {
      console.warn('No active cabinet. Use startDesign() first.');
      return null;
    }

    // Delegate to BaseCabinet instead of getAllModules().find()
    return this.currentCabinet.getModuleById(moduleId);
  }

  /**
   * OPTIMIZED: Delegate to BaseCabinet
   * Tìm module theo GLTF UUID (group.uuid)
   * @param {string} gltfUuid - UUID của THREE.Group object
   * @returns {Object|null} Module có group.uuid tương ứng
   */
  getModuleByGltfUuid(gltfUuid) {
    if (!this.currentCabinet) {
      console.warn('No active cabinet. Use startDesign() first.');
      return null;
    }

    // Delegate to BaseCabinet instead of getAllModules().find()
    return this.currentCabinet.getModuleByGroupUuid(gltfUuid);
  }

  /**
   * OPTIMIZED: Delegate to BaseCabinet
   * Lấy module gần nhất với vị trí 3D cho trước
   * @param {THREE.Vector3} position - Vị trí 3D
   * @param {number} maxDistance - Khoảng cách tối đa (mặc định: 1.0)
   * @returns {Object|null} Module gần nhất hoặc null
   */
  getModuleNearPosition(position, maxDistance = 1.0) {
    if (!this.currentCabinet) {
      console.warn('No active cabinet. Use startDesign() first.');
      return null;
    }

    // Delegate to BaseCabinet instead of manual iteration
    return this.currentCabinet.findModuleAtPosition(position, maxDistance);
  }

  /**
   * OPTIMIZED: Delegate to BaseCabinet
   * Lấy tất cả modules trong một khu vực cụ thể
   * @param {THREE.Box3} boundingBox - Hộp giới hạn khu vực
   * @returns {Array} Danh sách modules trong khu vực
   */
  getModulesInArea(boundingBox) {
    if (!this.currentCabinet) {
      console.warn('No active cabinet. Use startDesign() first.');
      return [];
    }

    // Delegate to BaseCabinet (cần thêm method này vào BaseCabinet)
    return this.currentCabinet.getModulesInArea(boundingBox);
  }

  /**
   * OPTIMIZED: Use BaseCabinet methods
   * Lấy thông tin tổng quan về tất cả modules
   * @returns {Object} Thống kê tổng quan
   */
  getModulesSummary() {
    if (!this.currentCabinet) {
      console.warn('No active cabinet. Use startDesign() first.');
      return {
        total: 0,
        byType: {},
        loaded: 0,
        visible: 0,
      };
    }

    // Use BaseCabinet methods for better performance
    const summary = {
      total: this.currentCabinet.getModuleCount(),
      byType: this.currentCabinet.getModuleTypeDistribution(),
      loaded: 0,
      visible: 0,
      totalWidth: 0,
      totalVolume: 0,
    };

    // Only iterate once for loaded/visible/size calculations
    const allModules = this.currentCabinet.getAllModules();
    allModules.forEach(module => {
      // Đếm trạng thái
      if (module.isLoaded) summary.loaded++;
      if (module.isVisible) summary.visible++;

      // Tính tổng kích thước
      if (module.actualSize) {
        summary.totalWidth += module.actualSize.x;
        summary.totalVolume +=
          module.actualSize.x * module.actualSize.y * module.actualSize.z;
      }
    });

    return summary;
  }

  /**
   * OPTIMIZED: Delegate filtering to BaseCabinet
   * Tìm kiếm modules theo nhiều tiêu chí
   * @param {Object} criteria - Tiêu chí tìm kiếm
   * @returns {Array} Danh sách modules phù hợp
   */
  searchModules(criteria = {}) {
    if (!this.currentCabinet) {
      console.warn('No active cabinet. Use startDesign() first.');
      return [];
    }

    // Delegate to BaseCabinet (cần thêm method này vào BaseCabinet)
    return this.currentCabinet.searchModules(criteria);
  }

  /**
   * OPTIMIZED: Use getModuleById instead of manual search
   * Lấy thông tin chi tiết của module bao gồm 3D object
   * @param {string} moduleId - ID của module
   * @returns {Object|null} Thông tin đầy đủ của module
   */
  getModuleDetailInfo(moduleId) {
    // Use optimized getModuleById
    const module = this.getModuleById(moduleId);
    if (!module) return null;

    return {
      // Thông tin cơ bản
      id: module.id,
      type: module.moduleType || module.cabinetType,

      // Thông tin 3D
      position: module.position ? module.position.toArray() : null,
      rotation: module.rotation ? module.rotation.toArray() : null,
      scale: module.scale ? module.scale.toArray() : null,
      actualSize: module.actualSize ? module.actualSize.toArray() : null,
      boundingBox: module.boundingBox || null,

      // Thông tin trạng thái
      isLoaded: module.isLoaded,
      isVisible: module.isVisible,
      isSelected: module.isSelected,
      isFitCeiling: module.fitCeiling,

      // Thông tin visual
      material: module.material,
      color: module.color,
      doorType: module.doorType,
      handleType: module.handleType,

      // 3D Object reference
      group: module.group, // THREE.Group object

      // Custom data
      customData: module.customData || {},

      // Module data gốc
      moduleData: module.moduleData || {},
    };
  }

  /**
   * NEW METHOD: Get detailed info by GLTF UUID
   * Lấy thông tin chi tiết của module theo GLTF UUID
   * @param {string} gltfUuid - UUID của THREE.Group object
   * @returns {Object|null} Thông tin đầy đủ của module
   */
  getModuleDetailInfoByGltfUuid(gltfUuid) {
    // Use optimized getModuleByGltfUuid
    const module = this.getModuleByGltfUuid(gltfUuid);
    if (!module) return null;

    return this.getModuleDetailInfo(module.id);
  }

  // =============== EXISTING METHODS ===============

  /**
   * Export dữ liệu thiết kế hiện tại
   */
  exportCurrentDesign() {
    if (!this.currentCabinet) return null;

    // Use BaseCabinet serialize method if available
    if (this.currentCabinet.serialize) {
      return this.currentCabinet.serialize();
    }

    // Fallback to manual export
    const allModules = this.currentCabinet.getAllModules();
    return {
      type: this.currentCabinet.type,
      id: this.currentCabinet.id,
      modules: allModules.map(module => ({
        id: module.id,
        type: module.moduleType || module.cabinetType,
        data: module.getData ? module.getData() : module.moduleData,
        position: module.position ? module.position.toArray() : null,
        rotation: module.rotation ? module.rotation.toArray() : null,
      })),
    };
  }

  /**
   * Lấy constraints của loại tủ
   */
  getCabinetConstraints(cabinetType) {
    const tempCabinet = this.createTempCabinet(cabinetType);
    return tempCabinet.getConstraints();
  }

  createTempCabinet(cabinetType) {
    switch (cabinetType) {
      case TypeCabinet.KITCHEN:
        return new KitchenCabinet(this.sceneManager);
      case TypeCabinet.WARDROBE:
        return new WardrobeCabinet(this.sceneManager);
      case TypeCabinet.SHOE:
        return new ShoeCabinet(this.sceneManager);
      case TypeCabinet.TV:
        return new TiviCabinet(this.sceneManager);
      case TypeCabinet.DECORATIVE:
        return new DecorativeCabinet(this.sceneManager);
      case TypeCabinet.BED:
        return new BedCabinet(this.sceneManager);
      case TypeCabinet.VANITY:
        return new VanityCabinet(this.sceneManager);
      case TypeCabinet.DESK:
        return new DeskCabinet(this.sceneManager);
      default:
        throw new Error(`Unknown cabinet type: ${cabinetType}`);
    }
  }

  /**
   * Xóa tất cả
   */
  clearAll() {
    if (this.currentCabinet) {
      this.currentCabinet.clearAllModules();
    }
    this.currentCabinet = null;
    this.cabinetHistory = [];
  }
}
